import * as React from 'react';

//other deps
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Localized } from '@fluent/react';
import Spinner from 'react-loader-spinner';
import { FluentBundle } from '@fluent/bundle';

//api
import * as api from 'app/api';

//redux
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { store } from 'src/main';

//api
import { driverTransportCertificate } from 'app/api';

//schemes
import { certificate, trailerIdentRules } from '../../../schemes';

//components
import Builder from 'app/containers/Builder';
import * as Icons from 'app/components/Icons';
import ImageModal from 'app/components/ImageModal';

//types
import { RootState } from 'app/reducers';

import {
	Certificate,
	CertificateCapabilityEnum,
	DocumentAttributesMap,
	DocumentTypeEnum,
	isStsMainPage,
	isStsOwnerPage,
} from 'app/models';

import { BidApiModel, DriverApiModel, RecognizeApiModel } from 'app/api/types';
import { DriverActions, UserActions } from 'app/actions';
import { Field, SchemeProps } from 'app/containers/Builder/props';

type CertificateType = 'transport' | 'trailer';

//utils
import { changeDateFormat } from 'app/utils';

//helpers
import { makeMasker, maskGetValue } from 'app/utils';
import { findTransportTypeByName, mapTransportBrands, mapTransportTypes } from '../../../../common/common.helpers';

//constants
import { VERIFIED } from '../docs.page';
import { documentLocalizedLabelMap, documentTypeMap } from './certificateItem.constants';

//----------------------------------------------------------
// CertificateItem
//----------------------------------------------------------
class CertificateItem extends React.Component<CertificateItem.Props, CertificateItem.State> {
	private builder: Builder | null = null;

	public state: CertificateItem.State = {
		scheme: _.cloneDeep(certificate),

		certificateType: 'transport',
		loading: false,
		editable: true,
		form: {},
	};

	constructor(props: CertificateItem.Props) {
		super(props);
	}

	private getLocalizedMessage = (id: string): string => {
		const { locales } = this.props;
		if (!locales) return '';

		const [locale] = locales;
		return locale.getMessage(id)?.value?.toString() || '';
	};

	public componentDidMount() {
		const { certificate } = this.props;

		if (certificate.capabilities.includes(CertificateCapabilityEnum.EDIT)) {
			this.updateSchemeWithProps();
		} else {
			this.setState({
				editable: false,
			});
		}
	}

	private updateSchemeWithProps = async () => {
		const { scheme } = this.state;
		const { transportTypes, trailerTypes, brands, certificate } = this.props;

		if (!transportTypes || !trailerTypes) return;

		const certificateKind = findTransportTypeByName({ ...transportTypes, ...trailerTypes }, certificate.kind);

		const transportKindInput = scheme[0] as SchemeProps.Select<string>;
		const transportBrandInput = scheme[1] as SchemeProps.Select<string>;
		const transportModelInput = scheme[2] as SchemeProps.Input;
		const transportIdentInput = scheme[6] as SchemeProps.Input;

		transportIdentInput.disabled = true;

		if (brands) {
			transportBrandInput.choices = mapTransportBrands(brands);
		}

		if (certificateKind) {
			this.setState({ certificateType: certificateKind.type as CertificateType });

			if (certificateKind.type === 'trailer') {
				transportKindInput.choices = mapTransportTypes(trailerTypes);
				transportKindInput.label = 'transports-certificate-trailer-kind';
				transportBrandInput.label = 'transports-certificate-trailer-brand';
				transportModelInput.label = 'transports-certificate-trailer-model';
				transportIdentInput.label = 'transports-certificate-trailer-ident';
				transportIdentInput.mask = 'ss0000000';
				transportIdentInput.rules = trailerIdentRules;
			} else {
				transportKindInput.choices = mapTransportTypes(transportTypes);
			}
		}

		this.setState({ scheme });
		await Promise.delay(70);

		this.updateBuilderValues(certificate);
	};

	private updateBuilderValues = (certificate: Partial<Certificate>) => {
		const { builder } = this;
		const { scheme } = this.state;

		if (!builder) return;

		const allowedStringFields = ['ident', 'model', 'vin', 'number', 'color', 'issuedAt', 'mainPage', 'ownerPage'];
		const allowedSelectFields = ['kind', 'brand', 'manufactureYear'];

		Object.keys(certificate).forEach((key) => {
			const ref = builder.formRefs.get(key);
			const value = certificate[key as keyof Certificate] || null;

			if (!ref || !value) return;

			if (allowedStringFields.includes(key)) {
				ref.updateValue(value);
			} else if (allowedSelectFields.includes(key)) {
				const schemeMap: Record<string, number> = {
					kind: 0,
					brand: 1,
				};

				const getChoice = (choices: Field.Choice<string>[], label: string) => {
					return choices.find((item) => item.label === label || item.value === label) || null;
				};

				switch (key) {
					case 'manufactureYear': {
						ref.updateValue({ label: value, value });
						break;
					}

					case 'kind':
					case 'brand': {
						const schemeIndex = schemeMap[key];
						const { choices = [] } = scheme[schemeIndex] as SchemeProps.Select<string>;
						const choice = getChoice(choices, value as string);

						if (choice) {
							ref.updateValue(choice);
						}
						break;
					}

					default:
						break;
				}
			}
		});
	};

	private mapRecognizedDocumentFields = <T extends keyof DocumentAttributesMap>({
		documentType,
		attributes,
	}: RecognizeApiModel.ProcessResponseData<T>) => {
		let certificatePatch: Partial<Certificate> = {};

		if (isStsOwnerPage(documentType, attributes)) {
			const { number, issuedAt } = attributes;

			certificatePatch = {
				number: number?.value || undefined,
				issuedAt: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', issuedAt?.value) || undefined,
			};
		} else if (isStsMainPage(documentType, attributes)) {
			const { number, vin, color, model, manufactureYear, vehicleType } = attributes;

			certificatePatch = {
				number: number?.value || undefined,
				vin: vin?.value || undefined,
				color: color?.value || undefined,
				model: model?.value || undefined,
				manufactureYear: manufactureYear?.value || undefined,
				kind: vehicleType?.kind?.id || undefined,
				brand: model?.brand?.id || undefined,
			};
		}

		return certificatePatch;
	};

	private documentRecognizeProcess = async (name: string, value: any) => {
		const documentType = documentTypeMap[name];
		if (!documentType || !value) return;

		let certificate: Partial<Certificate> = {};

		let failedMessage: string | null = null;
		const documentLocalizedName = documentLocalizedLabelMap[name];

		try {
			this.setState({ loading: true });

			const data = await api.recognizeProcess({
				documentType: documentType,
				documentFile: value,
			});

			this.setState({ loading: false });

			if (data.documentType === DocumentTypeEnum.Invalid) {
				failedMessage = documentLocalizedName;
			} else if (documentType !== data.documentType) {
				failedMessage = documentLocalizedName;
			} else {
				certificate = this.mapRecognizedDocumentFields(data);
			}
		} catch (error) {
			failedMessage = documentLocalizedName;
		}

		if (failedMessage) {
			const errorTitle = this.getLocalizedMessage('profile-alert-docs-recognize-failed');
			const errorDescription = this.getLocalizedMessage(failedMessage);
			const errorMessage = `${errorTitle} ${errorDescription}`;

			toast.error(errorMessage, {
				autoClose: 5000,
				position: toast.POSITION.BOTTOM_CENTER,
			});
		} else {
			toast.success(this.getLocalizedMessage('profile-alert-updated'), {
				position: toast.POSITION.BOTTOM_CENTER,
			});
		}

		if (!Object.keys(certificate).length) return;

		this.updateBuilderValues(certificate);
		this.builder?.isValid();
	};

	private onUpdate = (name: string, value: any) => {
		const { form } = this.state;

		this.setState({
			form: {
				...form,
				[name]: value,
			},
		});

		//[id-engine] recognize document
		this.documentRecognizeProcess(name, value);
	};

	private onSave = async () => {
		const { form, loading } = this.state;

		const { updateTransportCertificate, sessionToken, certificate, transportId } = this.props;

		if (sessionToken && updateTransportCertificate && !loading) {
			this.setState({ loading: true });

			try {
				const { builder } = this;

				const params: DriverApiModel.CertificateRequest = {
					...form,
					uuid: certificate.uuid,
				};

				if (builder) {
					builder.isValid();
				}

				if (form.number) {
					params.number = form.number.split(' ').join('').replace('№', '');
				}

				const updated = await driverTransportCertificate(transportId, params, 'patch');

				updateTransportCertificate({
					transportId,
					data: updated,
				});

				toast.success('Сохранено', {
					position: toast.POSITION.BOTTOM_CENTER,
				});

				this.setState({ loading: false });

				await Promise.delay(70);

				if (!certificate.capabilities.includes(CertificateCapabilityEnum.EDIT)) {
					this.setState({
						editable: false,
					});
				}

				// updating user state
				if (sessionToken) {
					await store.dispatch(UserActions.getProfile(sessionToken, true) as any);
				}
			} catch (error) {
				console.error(error);
			}

			this.setState({ loading: false });
		}
	};

	private outputModeratorMessages = (builder?: Builder | null) => {
		const {
			certificate: { verification },
		} = this.props;

		if (builder) {
			builder.outputOperatorCommentary(verification);
		}
	};

	private getForm = () => {
		const { scheme, loading } = this.state;
		const loadingClass = loading ? 'loading' : '';

		const certificateBuilderProps: Builder['props'] = {
			getRef: (node: Builder | null) => {
				this.builder = node;
				this.outputModeratorMessages(node);
			},
			onUpdate: this.onUpdate,
			scheme: scheme.map((el: SchemeProps.Union) => ({
				...el,
				disabled: loading,
			})),
		};

		return (
			<div className='form'>
				<Builder {...certificateBuilderProps} />

				<button disabled={loading} className={`action ${loadingClass}`} onClick={this.onSave}>
					{loading ? (
						<>
							<Spinner type='RevolvingDot' color='#1FB0E2' height={22} width={22} />
							<Localized id='profile-action-save-loading' />
						</>
					) : (
						<Localized id='transports-action-save' />
					)}
				</button>
			</div>
		);
	};

	private getPreview = () => {
		const { certificate } = this.props;

		const number = () => {
			const masker = makeMasker('00 00 № 000000', '');

			return !certificate.number ? '' : maskGetValue(certificate.number, undefined, masker);
		};

		return (
			<div className='preview'>
				<div className='row'>
					<div className='column'>
						<div className='field'>
							<div className='title'>
								<Localized id='transports-certificate-manufacture-year' />
							</div>

							<div className='text'>{certificate.manufactureYear}</div>
						</div>

						<div className='field'>
							<div className='title'>VIN</div>

							<div className='text'>{certificate.vin}</div>
						</div>
					</div>

					<div className='column'>
						<div className='field'>
							<div className='title'>
								<Localized id='transports-certificate-number' />
							</div>

							<div className='text'>{number()}</div>
						</div>

						<div className='field'>
							<div className='title'>
								<Localized id='transports-certificate-issued-at' />
							</div>

							<div className='text'>{certificate.issuedAt && moment(certificate.issuedAt).format('DD MMMM YYYY')}</div>
						</div>
					</div>
				</div>

				<div className='row'>
					<div className='column'>
						<div className='field photo'>
							<div className='title'>
								<Localized id='transports-certificate-preview-scan-title' />

								<div className='sub'>
									<Localized id='transports-certificate-main-page-sub' />
								</div>
							</div>

							<div className='image'>
								<ImageModal src={certificate.mainPage as string} />
							</div>
						</div>
					</div>

					<div className='column'>
						<div className='field photo'>
							<div className='title'>
								<Localized id='transports-certificate-preview-scan-title' />

								<div className='sub'>
									<Localized id='transports-certificate-owner-page-sub' />
								</div>
							</div>

							<div className='image'>
								<ImageModal src={certificate.ownerPage as string} />
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	private getHeader = () => {
		const {
			certificate: { ident, model },
			allVerified,
		} = this.props;

		const { editable, certificateType } = this.state;

		const header = ident ? ident : <Localized id='transports-number-unknown' />;
		const subheader = model ? model : <Localized id='transports-model-unknown' />;

		const headerPrefix = `transports-certificates-type-${certificateType}`;

		const toggleEditable = async () => {
			this.setState({ editable: true });

			await Promise.delay(250);
			this.updateSchemeWithProps();
		};

		return (
			<div className='titlebar'>
				<div className='titles'>
					<div className='header'>
						<Localized id={headerPrefix} />:{header}
					</div>
					<div className='subheader'>{subheader}</div>
				</div>

				<div className='actions'>
					{!editable && !allVerified && (
						<div className='edit' onClick={toggleEditable}>
							<Icons.Edit color='rgba(0,0,0, 0.7)' />
							<Localized id='profile-action-edit' />
						</div>
					)}
				</div>
			</div>
		);
	};

	public render() {
		const { certificate } = this.props;
		const { editable } = this.state;

		const header = this.getHeader();

		const isVerified = certificate && certificate.verification && certificate.verification.status === VERIFIED;
		const content = editable && !isVerified ? this.getForm() : this.getPreview();

		return (
			<div className='certificate item'>
				{header}
				{content}
			</div>
		);
	}
}

const mapStateToProps = ({ user, common }: RootState) => ({
	locales: common.bundlesLocales,
	sessionToken: user.sessionToken,
	brands: common.brands,
	trailerTypes: common.trailers,
	transportTypes: common.transports,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateTransportCertificate: DriverActions.updateTransportCertificate,
		},
		dispatch,
	);

export default connect<CertificateItem.StateProps, CertificateItem.DispatchProps, CertificateItem.ExternalProps>(
	mapStateToProps,
	mapDispatchToProps,
)(CertificateItem);

export namespace CertificateItem {
	export type Props = StateProps & DispatchProps & ExternalProps;

	// Props from redux mapState
	export interface StateProps {
		locales?: FluentBundle[];
		sessionToken?: string;
		brands?: BidApiModel.CommonMeta[];
		trailerTypes?: { [name: string]: BidApiModel.CommonMeta };
		transportTypes?: { [name: string]: BidApiModel.CommonMeta };
	}

	// Dispatch properties function from redux
	export interface DispatchProps {
		updateTransportCertificate?: (certificate: DriverActions.Payload.Docs<Certificate>) => void;
	}

	// Props from parent element e.g <Cmp custom={true} />
	export interface ExternalProps {
		certificate: Certificate;
		transportId: string;
		allVerified?: boolean;
	}

	// Main component state
	export interface State {
		scheme: SchemeProps.Union[];

		certificateType: CertificateType;
		form: { [name: string]: any };
		editable: boolean;
		loading: boolean;
	}
}
